package com.evoapps.evoshift.domain.model

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import kotlinx.serialization.Serializable

/**
 * SH-236: one part of a shift divided at a handover: someone fell ill (or had to leave) and a
 * colleague took over. The first part is the original shift document (its id is [groupId]), kept
 * by the original person and ending at [handoverAt]; the second is a new shift for the colleague,
 * starting at [handoverAt]. Both are ordinary shifts (older apps show them as such); this map
 * links them for the audit and for "Undo split" ([ShiftSplits.undo]).
 */
@Serializable
data class ShiftSplit(
    /** The original shift's id: the first part keeps it, the second points to it. */
    val groupId: String,
    /** 1: the original person's part (until the handover), 2: the colleague's (from it). */
    val part: Int,
    /** Local ISO time of the handover ("2026-02-01T12:00:00"). */
    val handoverAt: String,
    /** The planned end before the split (what "Undo split" puts back). */
    val originalPlannedEnd: String,
    /** The clocked end before the split, if there was one. */
    val originalActualEnd: String? = null,
    /** The manager's optional note ("Jan fell ill"), at most [ActualWork.MAX_NOTE] characters. */
    val reason: String? = null,
    /** The original person fell ill: an absence was offered for the rest of the day. */
    val sickness: Boolean = false,
    /** Who works the other part: the colleague on part 1, the original person on part 2. */
    val otherUserId: String? = null,
    val byUserId: String = "",
    /** When the split was made (ISO instant). */
    val at: String = ""
) {
    val isFirst: Boolean get() = part == FIRST
    val isSecond: Boolean get() = part == SECOND

    companion object {
        const val FIRST = 1
        const val SECOND = 2
    }
}

/** SH-236: why a shift cannot be split as asked (the screens word it). */
enum class SplitProblem {
    /** Nobody works the shift (an open shift nobody covered, or nobody came). */
    NOBODY_ON_SHIFT,
    /** It is already a part of a split (undo that first). */
    ALREADY_SPLIT,
    /** The handover is not strictly inside the shift. */
    HANDOVER_OUTSIDE,
    /** No colleague chosen, or the same person. */
    SAME_PERSON,
    /** The day's results are closed. */
    DAY_CLOSED,
    /** "Undo split": the other part is gone (deleted, or saved by an older app without the link). */
    OTHER_PART_MISSING
}

class ShiftSplitException(val problem: SplitProblem) : Exception("Cannot split the shift: $problem")

/** SH-236: the two parts of a split, as they will be written. */
data class SplitParts(val first: Shift, val second: Shift)

/**
 * SH-236: dividing a shift at a handover and putting it back together. Pure: the use cases
 * read and write, this works out the documents.
 */
object ShiftSplits {
    /**
     * The handover at [clock] ("HH:mm") on [shift]'s day, or on the next day for an overnight
     * shift whose clock time comes before its start; null when [clock] is not a time.
     */
    fun handoverAt(shift: Shift, clock: String): String? {
        val time = clock.trim().takeIf { it.length == 5 && it[2] == ':' } ?: return null
        val start = normal(shift.plannedTime.start) ?: return null
        val day = runCatching { LocalDate.parse(start.take(10)) }.getOrNull() ?: return null
        val sameDay = "${day}T$time:00"
        return if (sameDay > start) sameDay else "${day.plus(DatePeriod(days = 1))}T$time:00"
    }

    /**
     * Whether [handoverAt] lies strictly inside [shift]: after its planned start and before its
     * planned end, and also after the clock-in and before the clock-out where those were recorded.
     */
    fun isInside(shift: Shift, handoverAt: String): Boolean {
        val at = normal(handoverAt) ?: return false
        val start = normal(shift.plannedTime.start) ?: return false
        val end = normal(shift.plannedTime.end) ?: return false
        if (at <= start || at >= end) return false
        val actualStart = shift.actualTime?.start?.let(::normal)
        val actualEnd = shift.actualTime?.end?.let(::normal)
        if (actualStart != null && at <= actualStart) return false
        if (actualEnd != null && at >= actualEnd) return false
        return true
    }

    /** What stands in the way of splitting [shift] for [replacementId] at [handoverAt]; null: nothing. */
    fun problemOf(shift: Shift, handoverAt: String, replacementId: String?): SplitProblem? = when {
        shift.split != null -> SplitProblem.ALREADY_SPLIT
        shift.workerId == null -> SplitProblem.NOBODY_ON_SHIFT
        replacementId.isNullOrBlank() || replacementId == shift.workerId -> SplitProblem.SAME_PERSON
        !isInside(shift, handoverAt) -> SplitProblem.HANDOVER_OUTSIDE
        else -> null
    }

    /**
     * [shift] divided at [handoverAt]: the first part (the same document) keeps its person and
     * ends at the handover; the second (a new document, id "") is [replacementId]'s from the
     * handover to the planned end. Clock times follow: the first part's clock-out is the handover,
     * and the second part starts clocked in at the handover when the first was clocked in (and
     * keeps the clock-out when there was one). Breaks go to the part they were taken in.
     * [planned]: a split made ahead in the roster builder (the parts wait for publishing).
     */
    fun split(
        shift: Shift,
        handoverAt: String,
        replacementId: String,
        replacementName: String?,
        reason: String?,
        sickness: Boolean,
        editorUserId: String,
        now: String,
        planned: Boolean
    ): SplitParts {
        problemOf(shift, handoverAt, replacementId)?.let { throw ShiftSplitException(it) }
        val at = normal(handoverAt)!!
        val note = reason?.trim()?.take(ActualWork.MAX_NOTE)?.takeIf { it.isNotEmpty() }
        val actual = shift.actualTime
        val clockedIn = actual?.start?.isNotEmpty() == true
        val clockedOut = actual?.end?.isNotEmpty() == true
        val base = ShiftSplit(
            groupId = shift.id, part = ShiftSplit.FIRST, handoverAt = at,
            originalPlannedEnd = shift.plannedTime.end, originalActualEnd = actual?.end?.takeIf { it.isNotEmpty() },
            reason = note, sickness = sickness, byUserId = editorUserId, at = now
        )
        val managerStamp = ClockStamp(ClockSource.MANAGER)
        val (firstBreaks, secondBreaks) = shift.breaks.partition { (normal(it.start) ?: "") < at }
        val changed = planned && shift.isPublished
        val first = shift.copy(
            plannedTime = shift.plannedTime.copy(end = at),
            actualTime = if (clockedIn) actual!!.copy(end = at) else actual,
            clockAudit = if (clockedIn) (shift.clockAudit ?: ClockAudit(null, null)).copy(end = managerStamp) else shift.clockAudit,
            breaks = firstBreaks.map { b -> if (b.end.isEmpty() || (normal(b.end) ?: "") > at) b.copy(end = at) else b },
            hasUnpublishedChanges = shift.hasUnpublishedChanges || changed,
            split = base.copy(otherUserId = replacementId),
            metadata = shift.metadata.copy(updatedAt = now, updatedBy = editorUserId)
        )
        val second = shift.copy(
            id = "",
            userId = replacementId,
            userDisplayName = replacementName,
            actualUserId = null,
            notWorked = false,
            noShowReason = null,
            swapRequest = null,
            isOpen = false,
            conflictMetadata = null,
            absenceId = null,
            plannedTime = TimeRange(at, shift.plannedTime.end),
            actualTime = when {
                clockedIn && clockedOut -> TimeRange(at, actual!!.end)
                clockedIn -> TimeRange(at, "")
                else -> null
            },
            clockAudit = if (clockedIn) ClockAudit(managerStamp, if (clockedOut) shift.clockAudit?.end ?: managerStamp else null) else null,
            breaks = if (clockedIn) secondBreaks else emptyList(),
            clockCheck = null,
            unplanned = null,
            hasUnpublishedChanges = shift.hasUnpublishedChanges || changed,
            split = base.copy(part = ShiftSplit.SECOND, otherUserId = shift.workerId),
            metadata = AuditMetadata(createdAt = now, createdBy = editorUserId)
        )
        return SplitParts(first, second)
    }

    /**
     * The original shift back from its [first] part and [second] part ("Undo split"): the planned
     * end as before, the clock-out the colleague's (or the one before the split), every break.
     */
    fun undo(first: Shift, second: Shift?, editorUserId: String, now: String): Shift {
        val split = first.split ?: throw ShiftSplitException(SplitProblem.OTHER_PART_MISSING)
        val actual = first.actualTime?.takeIf { it.start.isNotEmpty() }?.let { a ->
            val end = second?.actualTime?.end?.takeIf { it.isNotEmpty() } ?: split.originalActualEnd.orEmpty()
            a.copy(end = end)
        } ?: first.actualTime
        val endStamp = if (second?.actualTime?.end?.isNotEmpty() == true) second.clockAudit?.end else first.clockAudit?.end
        return first.copy(
            plannedTime = first.plannedTime.copy(end = split.originalPlannedEnd),
            actualTime = actual,
            clockAudit = first.clockAudit?.copy(end = if (actual?.end.isNullOrEmpty()) null else endStamp),
            breaks = (first.breaks + second?.breaks.orEmpty()).sortedBy { it.start },
            split = null,
            metadata = first.metadata.copy(updatedAt = now, updatedBy = editorUserId)
        )
    }

    /** The other part of [shift]'s split among [shifts] (the same group, the other part); null: none. */
    fun otherPart(shift: Shift, shifts: List<Shift>): Shift? {
        val split = shift.split ?: return null
        return shifts.firstOrNull { it.id != shift.id && it.split?.groupId == split.groupId && it.split.part != split.part }
            ?: if (split.isSecond) shifts.firstOrNull { it.id == split.groupId } else null
    }

    /** "2026-02-01T12:00" and "2026-02-01T12:00:00" both as the latter; null when not a time. */
    internal fun normal(time: String): String? {
        val t = time.trim()
        if (t.length < 16 || t[10] != 'T') return null
        return if (t.length >= 19) t.take(19) else t.take(16) + ":00"
    }
}

/** SH-236: who actually worked a shift, as a manager records it afterwards. */
sealed interface ActualWorker {
    /** As planned: the assignee. */
    data object AsPlanned : ActualWorker
    /** Someone else (or the assignee, which is as planned). */
    data class Person(val userId: String) : ActualWorker
    /** Nobody came; [reason] is optional. */
    data class Nobody(val reason: String? = null) : ActualWorker
}

object ActualWork {
    /** The longest no-show reason or split note (firestore.rules checks the same). */
    const val MAX_NOTE = 300
}
